import type { FormlyFieldConfig } from '@ngx-formly/core';
import { DateHelperService } from '../../services/date-helper.service';

/**
 * Finds the field a `datetime` with `props.dateFromField` takes its date from, by keyPath with or
 * without its `entry.` head, or by key — the spellings module configs use. Never the asking field.
 * Nearest first: the subtree of each ancestor in turn, up to the root, so a row of a repeat (whose
 * rows share keys) finds its own date rather than row 0's.
 */
export function findDateSource(field: FormlyFieldConfig, path: string): FormlyFieldConfig | undefined {
  const bare = path.replace(/^entry\./, '');
  const matches = (f: FormlyFieldConfig) => {
    const keyPath = typeof f.props?.['keyPath'] === 'string' ? (f.props['keyPath'] as string).replace(/^entry\./, '') : undefined;
    return keyPath === bare || (keyPath === undefined && f.key === bare);
  };
  const walk = (f: FormlyFieldConfig): FormlyFieldConfig | undefined => {
    if (f !== field && f.formControl && matches(f)) return f;
    for (const child of f.fieldGroup ?? []) {
      const hit = walk(child);
      if (hit) return hit;
    }
    return undefined;
  };
  for (let scope = field.parent; scope; scope = scope.parent) {
    const hit = walk(scope);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * The day of a date field's value as `YYYY-MM-DD`, or null. Accepts what the date fields hold in
 * the course of an edit: an ISO string, a `Date`, or the picker's Luxon object (`ts`) that the date
 * field converts a tick later.
 */
export function dayOf(value: unknown): string | null {
  if (value == null || value === '') return null;
  const ts = (value as { ts?: unknown }).ts;
  const input = typeof ts === 'number' ? new Date(ts) : value;
  if (!(input instanceof Date) && typeof input !== 'string') return null;
  return DateHelperService.dateTimeToDateAndTime(input).date;
}

/** Date + time as the local ISO datetime the field stores; nothing unless both are there. */
export function composeDateTime(day: string | null, time: string | null): string | null {
  if (!day || !time) return null;
  // Not dateAndTimeToDateTime: it parses the bare day as UTC midnight, the previous day west of UTC.
  return DateHelperService.toLocalISOString(new Date(`${day}T${time}:00`));
}
